import type { ReactNode } from 'react';

/**
 * Un campo de un modal de corrección, con su casilla «cambiar» delante.
 *
 * ---------------------------------------------------------------------------
 * QUÉ RESUELVE, Y POR QUÉ ES COMPARTIDO
 *
 * Nació dentro de `CanjesBandeja` y se sacó acá cuando la bandeja de levantamiento estrenó su
 * propio modal de corrección (0059). Las dos pantallas prometen en pantalla exactamente lo mismo
 * —«los que no marques quedan deshabilitados mostrando lo que dice hoy el registro»— y esa promesa
 * la sostienen tres detalles que no se pueden escribir dos veces sin que se separen: dónde va la
 * casilla, que el control apagado SE VEA apagado, y dónde se lee el aviso de "no se puede vaciar".
 *
 * ---------------------------------------------------------------------------
 * DOS DECISIONES QUE PARECEN ESTILO Y NO LO SON
 *
 * 1. **Declarado en su propio módulo, nunca dentro del componente que lo usa.** Un componente
 *    definido adentro de otro se recrea en cada render y React desmonta y vuelve a montar su
 *    subárbol: el foco se pierde tecla a tecla (BITACORA §3, el formulario de registro quedó
 *    inusable por eso y tiene pruebas de regresión). Acá el daño sería peor todavía, porque son
 *    ocho campos de texto seguidos.
 *
 * 2. **El control llega por `children` y no se construye acá con un prop `tipo`.** Son fecha, hora,
 *    texto, número, select y textarea; una fábrica que los cubra todos termina siendo más difícil
 *    de leer que escribir cada input donde se usa.
 *
 * `C` es el tipo de las claves de campo de CADA pantalla (las de canjes no son las de
 * levantamiento). Genérico y no `string` a secas para que marcar un campo que no existe en esa
 * pantalla sea un error de compilación y no un checkbox que no hace nada.
 */
export interface CampoCorregibleProps<C extends string> {
  etiqueta: string;
  campo: C;
  marcados: ReadonlySet<C>;
  onAlternar: (campo: C, marcar: boolean) => void;
  /**
   * ¿Este campo se puede dejar vacío para borrar el dato?
   *
   * Llega como función y no como booleano suelto para que la pantalla pase LA MISMA lista que usa
   * su diff para decidir qué viaja. Escrito a mano campo por campo, el aviso seguiría diciendo
   * «obligatorio» para siempre el día que se afloje un NOT NULL — ya pasó dos veces en este
   * proyecto (`cliente_nombre` en la 0030, `foto_boleta_path` en la 0036).
   */
  sePuedeVaciar: (campo: C) => boolean;
  ayuda?: string;
  ancho?: 'completo';
  children: ReactNode;
}

export function CampoCorregible<C extends string>(
  { etiqueta, campo, marcados, onAlternar, sePuedeVaciar, ayuda, ancho, children }:
  CampoCorregibleProps<C>,
) {
  const marcado = marcados.has(campo);
  return (
    <div className={ancho === 'completo' ? 'col-span-2' : ''}>
      {/* La casilla va en su PROPIO `label`, no envolviendo al control: un `label` que abrace a
          los dos haría que escribir en el campo alterne la casilla. */}
      <label className="mb-0.5 flex items-center gap-1.5">
        <input type="checkbox" checked={marcado}
          aria-label={`Cambiar ${etiqueta}`}
          onChange={(e) => onAlternar(campo, e.target.checked)} />
        <span className={marcado ? 'font-medium' : 'text-gray-500'}>{etiqueta}</span>
      </label>
      <div className={marcado ? '' : 'opacity-60'}>{children}</div>
      {marcado && !sePuedeVaciar(campo) && (
        <p className="mt-0.5 text-xs text-amber-700">
          Es obligatorio: se cambia por otro valor, no se puede vaciar.
        </p>
      )}
      {ayuda !== undefined && <p className="mt-0.5 text-xs text-gray-500">{ayuda}</p>}
    </div>
  );
}
